import { useMemo } from 'react';

import { css } from '@emotion/react';
import { Box, BoxProps } from '@mui/system';
import { RemirrorRenderer } from '@remirror/react-renderer';
import { RichTextContent } from '@scorenco/core';

const useRichTextToProps = (content?: RichTextContent) =>
  useMemo(() => {
    if (!content) {
      return {};
    }

    if (typeof content === 'string') {
      return { dangerouslySetInnerHTML: { __html: content } };
    }

    const rematchRegex = /https:\/\/www\.rematch\.tv\/video\/([a-zA-Z0-9]+)/g;
    const textWithRematchLink = content.content?.[0]?.content?.[0]?.text;

    if (textWithRematchLink && rematchRegex.test(textWithRematchLink)) {
      const replacedText = textWithRematchLink.replace(
        rematchRegex,
        (match, videoId) => {
          const embedUrl = `https://www.rematch.tv/video/embed/${videoId}`;
          return (
            '<a href="' +
            match +
            '" target="_blank">' +
            match +
            '</a>' +
            '<div style="position: relative; overflow: hidden; padding-top: 56.25%; margin-top: 0.5rem;">' +
            '<iframe ' +
            'src="' +
            embedUrl +
            '" frameborder="0" scrolling="no" allowfullscreen="true" ' +
            'webkitallowfullscreen="true" mozallowfullscreen="true" width="100%" height="100%" ' +
            'style="position: absolute; top: 0; left: 0;" />' +
            '</div>'
          );
        }
      );

      return { dangerouslySetInnerHTML: { __html: replacedText } };
    }

    return {
      children: <RemirrorRenderer json={content} />,
    };
  }, [content]);

export const RichText = ({ content, ...rest }: RichTextProps) => {
  const richTextProps = useRichTextToProps(content);

  return (
    <Box
      css={css`
        p {
          margin: 0 0.125;
        }
        a {
          text-decoration: underline;
        }
      `}
      {...rest}
      {...richTextProps}
    />
  );
};

export type RichTextProps = BoxProps & {
  content?: RichTextContent;
};
